// Phase 31 — "I bought this property" modal.
//
// Opens from the Deal Detail header CTA (or the Dashboard quick-action).
// Calls POST /api/deals/{id}/mark-as-bought, then closes and toasts a
// "View in portfolio" link to /portfolio?filter=owned.
//
// All fields default to smart values pulled from the deal so most users
// just confirm and submit.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, Home, Calendar } from "lucide-react";
import { toast } from "sonner";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function MarkAsBoughtModal({ open, onOpenChange, deal, onMarked, onShareMilestone }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isSpanish = (i18n.language || "en").toLowerCase().startsWith("es");

  const STR = {
    title: isSpanish ? "Compré esta propiedad" : "I bought this property",
    subtitle: isSpanish
      ? "Marca esta operación como propiedad para seguir el desempeño real — historial de renta, IRR a la fecha, métricas."
      : "Mark this deal as owned so you can track actuals — rent history, IRR-to-date, performance.",
    purchaseDate: isSpanish ? "Fecha de compra" : "Purchase date",
    purchasePrice: isSpanish ? "Precio de compra real" : "Actual purchase price",
    repairCost: isSpanish ? "Costo de reparación inicial real" : "Actual initial repair cost",
    rent: isSpanish ? "Renta mensual al inicio" : "Actual monthly rent at move-in",
    note: isSpanish ? "Nota (opcional)" : "Note (optional)",
    notePh: isSpanish ? "Financiado por..., cerrado vía..." : "Closed via..., financed by...",
    caption: isSpanish
      ? "Se agregará a tu portafolio de propiedades y activará el seguimiento de desempeño."
      : "We'll add this to your owned portfolio and unlock performance tracking.",
    submit: isSpanish ? "Marcar como comprada" : "Mark as bought",
    cancel: isSpanish ? "Cancelar" : "Cancel",
    successToast: isSpanish ? "¡Propiedad! Siguiendo desempeño." : "Owned! Tracking performance.",
    viewLink: isSpanish ? "Ver en portafolio" : "View in portfolio",
    shareMilestone: isSpanish ? "Compartir hito" : "Share milestone",
    undo: isSpanish ? "Deshacer" : "Undo",
    undoToast: isSpanish ? "Marca revertida." : "Mark reverted.",
    undoFailed: isSpanish ? "No se pudo deshacer (ventana expirada)." : "Could not undo (window expired).",
  };

  const dealAsk = deal?.counter_offer || deal?.asking_price || deal?.purchase_price || 0;
  const dealRepair = deal?.repairs || deal?.repair_budget || 0;
  const dealRent = deal?.monthly_rent || 0;

  const [purchaseDate, setPurchaseDate] = useState(todayISO());
  const [price, setPrice] = useState("");
  const [repair, setRepair] = useState("");
  const [rent, setRent] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset fields when reopened with a fresh deal.
  useEffect(() => {
    if (open) {
      setPurchaseDate(todayISO());
      setPrice(String(Math.round(dealAsk)));
      setRepair(String(Math.round(dealRepair)));
      setRent(String(Math.round(dealRent)));
      setNote("");
    }
  }, [open, dealAsk, dealRepair, dealRent]);

  const submit = async () => {
    const priceNum = parseFloat(price);
    const repairNum = parseFloat(repair) || 0;
    const rentNum = parseFloat(rent) || 0;
    if (!purchaseDate) {
      toast.error(isSpanish ? "La fecha es requerida" : "Date is required");
      return;
    }
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      toast.error(isSpanish ? "Precio inválido" : "Invalid price");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/deals/${deal.id}/mark-as-bought`, {
        purchase_date: purchaseDate,
        purchase_price_paid: priceNum,
        actual_repair_cost: repairNum,
        actual_monthly_rent: rentNum,
        note: note.trim() || undefined,
      });
      toast.success(STR.successToast, {
        // Phase 33 — primary action is **Undo** (most urgent, 5s window).
        // Secondary action (cancel slot) is the share-milestone CTA.
        action: {
          label: STR.undo,
          onClick: async () => {
            try {
              const ur = await api.post(`/deals/${deal.id}/unmark-as-bought-own`);
              onMarked?.(ur.data);
              toast.success(STR.undoToast);
            } catch (err) {
              const status = err?.response?.status;
              toast.error(status === 410 ? STR.undoFailed : STR.undoFailed);
            }
          },
        },
        cancel: onShareMilestone ? {
          label: STR.shareMilestone,
          onClick: () => {
            onOpenChange(false);
            setTimeout(() => onShareMilestone?.(), 150);
          },
        } : {
          label: STR.viewLink,
          onClick: () => navigate("/portfolio?filter=owned"),
        },
        duration: 5000,
      });
      onMarked?.(data);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : detail?.message || e.message || "Failed";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mark-as-bought-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="h-4 w-4 text-emerald-600" /> {STR.title}
          </DialogTitle>
          <DialogDescription>{STR.subtitle}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid gap-1.5">
            <Label htmlFor="mab-date" className="text-xs">
              <Calendar className="me-1 inline h-3 w-3" /> {STR.purchaseDate}
            </Label>
            <Input
              id="mab-date"
              type="date"
              value={purchaseDate}
              max={todayISO()}
              onChange={(e) => setPurchaseDate(e.target.value)}
              data-testid="mab-purchase-date"
              className="h-9 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="mab-price" className="text-xs">{STR.purchasePrice}</Label>
              <Input
                id="mab-price"
                type="number"
                min="1"
                step="100"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                data-testid="mab-purchase-price"
                className="h-9 text-sm"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="mab-repair" className="text-xs">{STR.repairCost}</Label>
              <Input
                id="mab-repair"
                type="number"
                min="0"
                step="100"
                value={repair}
                onChange={(e) => setRepair(e.target.value)}
                data-testid="mab-repair-cost"
                className="h-9 text-sm"
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="mab-rent" className="text-xs">{STR.rent}</Label>
            <Input
              id="mab-rent"
              type="number"
              min="0"
              step="50"
              value={rent}
              onChange={(e) => setRent(e.target.value)}
              data-testid="mab-monthly-rent"
              className="h-9 text-sm"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="mab-note" className="text-xs">{STR.note}</Label>
            <Textarea
              id="mab-note"
              rows={2}
              maxLength={1000}
              placeholder={STR.notePh}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="mab-note"
              className="text-sm"
            />
          </div>
          <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
            {STR.caption}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="mab-cancel-btn"
          >
            {STR.cancel}
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="mab-submit-btn"
          >
            {submitting ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="me-1 h-3.5 w-3.5" />}
            {STR.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
